<template>
  <div class="bg-marketing-light pb-5">
    <!-- Breadcrumb Section -->
    <section class="bg-white py-3" style="border-bottom: 1px solid #E2E8F0;">
      <CContainer>
        <div class="d-flex align-items-center small text-muted">
          <router-link to="/" class="text-marketing-gray-600 font-weight-500" style="text-decoration: none;">Home</router-link>
          <CIcon name="cil-chevron-right" size="sm" class="mx-2"/>
          <router-link to="/features" class="text-marketing-gray-600 font-weight-500" style="text-decoration: none;">Features</router-link>
          <CIcon name="cil-chevron-right" size="sm" class="mx-2"/>
          <span class="text-marketing-dark font-weight-bold">{{ currentFeature.name }}</span>
        </div>
      </CContainer>
    </section>

    <!-- Hero Section -->
    <section class="py-5 bg-white">
      <CContainer>
        <CRow class="justify-content-center">
          <CCol lg="8" class="text-center">
            <div class="marketing-icon-wrapper mx-auto mb-4" style="width: 80px; height: 80px; font-size: 2rem;">
              <CIcon :name="currentFeature.icon" size="custom-size" style="width: 40px; height: 40px;"/>
            </div>
            <h1 class="display-4 font-weight-bold marketing-heading mb-3">
              {{ currentFeature.title }}
            </h1>
            <p class="lead marketing-body mb-0">
              {{ currentFeature.heroDescription }}
            </p>
          </CCol>
        </CRow>
      </CContainer>
    </section>

    <!-- Detailed Content Area -->
    <section class="py-5">
        <CContainer>
          <CRow>
              <CCol md="8" class="mx-auto">
                 <CCard class="border-0 shadow-sm marketing-card-feature" style="background: white;">
                    <CCardBody class="p-5">
                        <h2 class="h3 font-weight-bold text-marketing-dark mb-4">Why Niagaku {{ currentFeature.name }}?</h2>
                        <div v-for="(benefit, index) in currentFeature.benefits" :key="index" class="mb-4">
                            <div class="d-flex align-items-start mb-2">
                                <CIcon name="cil-check-circle" class="text-marketing-accent mr-3 mt-1" size="lg"/>
                                <div>
                                    <strong class="marketing-subheading d-block h5 mb-2">{{ benefit.title }}</strong>
                                    <p class="marketing-body text-muted mb-0">{{ benefit.description }}</p>
                                </div>
                            </div>
                        </div>

                        <div class="border-top pt-5 text-center mt-5">
                            <h3 class="h4 font-weight-bold mb-3">Ready to Streamline Your Business?</h3>
                            <CButton size="lg" class="btn-marketing-accent px-5 py-3 font-weight-600 btn-scale" to="/signup">
                              Start Free 30-Day Trial
                            </CButton>
                        </div>
                    </CCardBody>
                 </CCard>
              </CCol>
          </CRow>
        </CContainer>
    </section>
  </div>
</template>

<script>
export default {
  name: 'FeatureDetail',
  data() {
      return {
          featureConfig: {
              'payroll': {
                  name: 'Payroll',
                  icon: 'cil-money',
                  title: 'Automated Malaysian Payroll',
                  heroDescription: 'Process payroll in minutes with one-click LHDN, EPF, SOCSO, and EIS calculations—tailored perfectly for Malaysian businesses.',
                  benefits: [
                      { title: 'Always Compliant', description: 'Our tables are always updated to the latest tax brackets and government regulations so you never miscalculate deductions.' },
                      { title: '1-Click Statutory Submissions', description: 'Automatically generate txt files for EPF and SOCSO bulk uploads directly to government portals.' },
                      { title: 'Electronic Payslips', description: 'Automatically email password-protected electronic payslips to all your employees instantly.' }
                  ]
              },
              'invoicing': {
                  name: 'Invoicing',
                  icon: 'cil-file',
                  title: 'Professional Invoicing & Quotes',
                  heroDescription: 'Create beautiful, professional invoices in seconds. Get paid faster and track outstanding payments effortlessly.',
                  benefits: [
                      { title: 'Customizable Templates', description: 'Add your company logo, choose colors, and select templates that accurately reflect your brand identity.' },
                      { title: 'Automated Reminders', description: 'Stop chasing payments. Niagaku automatically sends gentle email reminders to clients before and after invoices are due.' },
                      { title: 'Seamless Quotation to Invoice', description: 'Convert accepted quotations straight into live invoices with a single click—no data re-entry required.' }
                  ]
              },
              'accounting': {
                  name: 'Accounting',
                  icon: 'cil-chart-pie',
                  title: 'Powerful Cloud Accounting',
                  heroDescription: 'Real-time financial insights, completely automated double-entry bookkeeping, and perfectly organized cash flow.',
                  benefits: [
                      { title: 'Live Financial Dashboards', description: 'View your Profit & Loss and Balance Sheet in real-time. Know exactly where your business stands at a glance.' },
                      { title: 'Bank Reconciliation', description: 'Connect your bank accounts securely and automatically match transactions to speed up end-of-month closing.' },
                      { title: 'Accountant Ready', description: 'Give your accountant secure, restricted access to easily export reports and audit trails for tax filing.' }
                  ]
              },
              'expenses': {
                  name: 'Expenses',
                  icon: 'cil-wallet',
                  title: 'Smarter Expense Management',
                  heroDescription: 'Track every Ringgit seamlessly. Categorize expenses and eliminate receipt chaos from your office.',
                  benefits: [
                      { title: 'Digital Receipt Capture', description: 'Snap a picture of your receipt and upload it instantly. We safely store your digital records for LHDN audits.' },
                      { title: 'AI-Assisted Categorization', description: 'Our smart algorithm learns your regular expenses and automatically assigns the correct Chart of Accounts category.' },
                      { title: 'Employee Claim Management', description: 'Employees can submit expense claims digitally for direct approval and automated reimbursement via payroll.' }
                  ]
              },
              'inventory': {
                  name: 'Inventory',
                  icon: 'cil-basket',
                  title: 'Multi-Location Inventory',
                  heroDescription: 'Never run out of stock again. Track products across multiple warehouses with real-time stock alerts.',
                  benefits: [
                      { title: 'Real-Time Stock Levels', description: 'Stock levels update automatically the moment an invoice is generated or a purchase order is received.' },
                      { title: 'Low Stock Alerts', description: 'Set minimum threshold levels and receive automatic email notifications when it is time to reorder products.' },
                      { title: 'Profit Margin Tracking', description: 'Instantly see the gross margin on every product variant you sell to maximize your profitability.' }
                  ]
              },
              'multi-user': {
                  name: 'Multi-User Collaboration',
                  icon: 'cil-people',
                  title: 'Team-Based Role Access',
                  heroDescription: 'Collaborate safely. Give your team access to the tools they need while protecting sensitive financial data.',
                  benefits: [
                      { title: 'Custom Role Definitions', description: 'Create specific roles like "Sales Staff", "Bookkeeper", or "Manager" with granular permission controls.' },
                      { title: 'Data Privacy Protection', description: 'Keep employee salaries private. Only authorized Admin users can view the payroll and sensitive company modules.' },
                      { title: 'Comprehensive Audit Trails', description: 'Track exactly who created, modified, or deleted any record in the system for complete accountability.' }
                  ]
              }
          }
      };
  },
  computed: {
      currentFeature() {
          const slug = this.$route.params.slug;
          // Return the matching feature, or a generic fallback if slug not found
          return this.featureConfig[slug] || {
              name: 'Feature Not Found',
              icon: 'cil-warning',
              title: 'We could not find this feature.',
              heroDescription: 'The feature you are looking for does not exist or has been moved.',
              benefits: []
          };
      }
  },
  metaInfo() {
      return {
          title: `${this.currentFeature.name} - Niagaku Features`,
          meta: [
              {
                  name: 'description',
                  content: this.currentFeature.heroDescription
              }
          ]
      }
  }
}
</script>

<style scoped>

</style>
